import React, { memo, useEffect, useMemo, useState } from "react";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
    DropdownMenuLabel,
    DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { cn, putComma } from "@/lib/utils";
import { Inter } from "next/font/google";
import { useSubscribeStock, WebSocketSample } from "@/contexts/WebSocket";
import { Mixpanel } from "@/elements/Mixpanel";
import { getInstrument } from "@/lib/tradeCards";
import RatioBar from "@/elements/RatioBar/RatioBar";
import { useSelector } from "react-redux";
const inter = Inter({
    subsets: ["latin"],
    variable: "--font-inter"
});
// import { useSubscription } from 'react-stomp-hooks';

function MarketDepth({ children, scriptCode, source, symbol }) {
    const [open, setOpen] = useState(false);
    const stockData = useSelector((state) => state.livePrice.subscriptedStocks);

    const MBP = useMemo(
        () => stockData?.[scriptCode]?.[WebSocketSample.MBP]?.levels ?? [],
        [stockData?.[scriptCode]?.[WebSocketSample.MBP]]
    );
    const TOPBIDASK = useMemo(
        () => stockData?.[scriptCode]?.[WebSocketSample.TOPBIDASK],
        [stockData?.[scriptCode]?.[WebSocketSample.TOPBIDASK]]
    );

    useEffect(() => {
        if (open) {
            Mixpanel.track("cta_clicked", {
                page: source,
                cta_text: "market_depth",
                symbol: symbol,
                expiry_date: null,
                instrument_name: getInstrument(symbol)
            });
        }
    }, [open]);

    return (
        <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
            <DropdownMenuContent className={cn("w-80 rounded-2xl p-2 font-Inter shadow-2xl", inter.variable)}>
                <DropdownMenuLabel>Market depth</DropdownMenuLabel>
                <DropdownMenuSeparator />

                {MBP && (
                    <div className="mt-4 font-Inter flex gap-6 px-4">
                        <div className="basis-1/2">
                            <div className="flex justify-between">
                                <div className="text-xs text-[#414141] font-medium">Qty.</div>
                                <div className="text-xs text-[#414141] font-medium">Bid</div>
                            </div>
                            <div className="flex flex-col gap-2.5 py-3">
                                {MBP?.map((ele, i) => {
                                    let maxQuantity = Math.max(...MBP?.map((ele) => ele?.ibuyQty));
                                    return (
                                        <div
                                            className="flex justify-between p-1 overflow-hidden rounded relative "
                                            key={i}
                                        >
                                            <div
                                                className="ease-in-out duration-300 rounded-s absolute left-0 top-0 bottom-0 bg-[#DCEDE7] "
                                                style={{ right: `${100 - (ele.ibuyQty / maxQuantity) * 100}%` }}
                                            />
                                            <div className="text-[10px] text-[#414141] font-medium z-[1]">
                                                {ele.ibuyQty}
                                            </div>
                                            <div className="text-[10px] text-[#414141] font-medium z-[1]">
                                                {ele.fbuyPrice?.toFixed(2)}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="basis-1/2">
                            <div className="flex justify-between">
                                <div className="text-xs text-[#414141] font-medium">Ask</div>
                                <div className="text-xs text-[#414141] font-medium">Qty.</div>
                            </div>
                            <div className="flex flex-col gap-2.5 py-3">
                                {MBP?.map((ele, i) => {
                                    let maxQuantity = Math.max(...MBP?.map((ele) => ele?.isellQty));
                                    return (
                                        <div
                                            className="flex justify-between p-1 rounded overflow-hidden relative"
                                            key={i}
                                        >
                                            <div
                                                className="ease-in-out duration-300 rounded-e absolute right-0 top-0 bottom-0 bg-[#FAE6DF] "
                                                style={{ left: `${100 - (ele.isellQty / maxQuantity) * 100}%` }}
                                            />
                                            <div className="text-[10px] text-[#414141] font-medium z-[1]">
                                                {ele.fsellPrice?.toFixed(2)}
                                            </div>
                                            <div className="text-[10px] text-[#414141] font-medium z-[1]">
                                                {ele.isellQty}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}
                {TOPBIDASK && (
                    <div className="p-4">
                        <div className="flex flex-row justify-between mb-2">
                            <div className="text-xs font-medium text-[#414141]">Total Buy Qty.</div>
                            <div className="text-xs font-medium text-[#414141]">Total Sell Qty.</div>
                        </div>
                        <RatioBar
                            percent={
                                (TOPBIDASK?.iTotalBuyQty * 100) / (TOPBIDASK?.iTotalBuyQty + TOPBIDASK?.iTotalSellQty)
                            }
                            className="w-full"
                        />
                        <div className="flex flex-row justify-between mt-2 ">
                            <div className="text-xs font-medium">
                                <b>
                                    {(
                                        (TOPBIDASK?.iTotalBuyQty * 100) /
                                        (TOPBIDASK?.iTotalBuyQty + TOPBIDASK?.iTotalSellQty)
                                    )?.toFixed(2)}
                                    %
                                </b>{" "}
                                ({putComma(TOPBIDASK?.iTotalBuyQty, 0)})
                            </div>
                            <div className="text-xs font-medium">
                                ({putComma(TOPBIDASK?.iTotalSellQty, 0)}){" "}
                                <b>
                                    {(
                                        (TOPBIDASK?.iTotalSellQty * 100) /
                                        (TOPBIDASK?.iTotalBuyQty + TOPBIDASK?.iTotalSellQty)
                                    )?.toFixed(2)}
                                    %
                                </b>
                            </div>
                        </div>
                    </div>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default memo(MarketDepth);
